import React, { useMemo, useState } from "react"
import { Box, Button, Center, Container, Heading } from "@chakra-ui/react"

import { SectionProps, withSection } from "@app/hoc/Section"
import ProductCard from "@components/Product/ProductCard"
import { Location } from "@root/types/global"
import TileCarousel from "@components/TileCarousel/TileCarousel"
import useFeaturedProducts from "@hooks/useFeaturedProducts"
import { useRoutes } from "@hooks/useRoutes"
import Link from "@components/Link"
import ProductCardDrawer from "@components/ProductCardDrawer"
import { useMedia } from "@hooks/useMedia"

export type FeaturedProductsProps = GatsbyTypes.SanitySectionFeaturedProducts &
  SectionProps & {
    products: GatsbyTypes.SanityProduct[]
    location: Location
  }

const FeaturedProducts = ({
  title,
  link,
  theme,
  carouselSettings,
  collection,
  products,
  location,
  innerRef,
  removeTopPadding,
  removeBottomPadding,
  handleTrackingClick,
  externalLink,
}: FeaturedProductsProps) => {
  const { urlResolver } = useRoutes()
  const [quickViewProduct, setQuickViewProduct] = useState<BundleProduct | null>(null)
  const { isBase } = useMedia()
  const items = useFeaturedProducts(collection, products);

  const validItems = React.useMemo(() => {
    return items?.filter(item => item?.sanityProduct?.shopify?.published !== false)
  }, [items])

  const resolvedLink = urlResolver(link)
  const linkURL = externalLink ? externalLink : urlResolver(link)
  const spaceBetweenTiles = isBase ? 8 : 16

  const hasProductBadge = useMemo(() => items?.some(item => item?.product?.tags?.find(tag => tag?.includes("label"))), [items])

  return validItems?.length ? (
    <>
      <Box
        ref={innerRef}
        bg={theme === "Dark" ? "brand.beige.dark" : "white"}
        paddingTop={removeTopPadding ? 0 : 5}
        paddingBottom={removeBottomPadding ? 0 : 5}
        pr={0}
      >
        <Container
          as="section"
          overflow={`hidden`}
          sx={{
            ".swiper": {
              overflow: "visible",
            },
          }}
        >
          <Heading as="h2" size="h2" mb={2.5} textAlign="center">
            {title}
          </Heading>
          <TileCarousel
            carouselSettings={carouselSettings}
            spaceBetweenTiles={spaceBetweenTiles}
            slidesPerView={6}
            slidesPerViewMobile={1.65}
            navigationProps={{ transform: `50%`, top: "calc(490px / 2)", bg: "brand.primary", color: "black" }}
          >
            {validItems?.map((item: any, idx: number) => (
              <Box key={idx}>
                <ProductCard
                  location={location}
                  item={item.product}
                  fullSize={true}
                  handleTrackingClick={handleTrackingClick}
                  onQuickView={setQuickViewProduct}
                  showBadgePlaceholder={hasProductBadge}
                />
              </Box>
            ))}
          </TileCarousel>
          {link && (
            <Center mt={2.5}>
              <Button as={Link} to={externalLink ? linkURL : resolvedLink.url} variant="blue">
                {resolvedLink.title}
              </Button>
            </Center>
          )}
        </Container>
      </Box>
      {quickViewProduct && (
        <ProductCardDrawer
          product={quickViewProduct}
          location={location}
          updateQuickViewProduct={product => setQuickViewProduct(product)}
          onClose={() => setQuickViewProduct(null)}
        />
      )}
    </>
  ) : (
    <></>
  )
}

export default React.memo(withSection(FeaturedProducts))
